import { forwardRef, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { formatTimeAgo } from '@/lib/format'
import { useAddNote } from '../../hooks/useAlertActions'
import type { AlertNote } from '../../types'

interface Props {
  alertId: string
  notes: AlertNote[]
}

/**
 * AF-126 — vertical notes timeline with an inline add-note form below.
 * Notes are append-only (no edit/delete) so the audit trail is clean.
 */
export const NotesTimeline = forwardRef<HTMLDivElement, Props>(function NotesTimeline(
  { alertId, notes },
  ref,
) {
  return (
    <Card ref={ref}>
      <CardHeader>
        <CardTitle>Notes ({notes.length})</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {notes.length === 0 ? (
          <p className="text-sm text-muted-fg">No notes yet.</p>
        ) : (
          <ol className="relative flex flex-col gap-3 border-l border-border pl-4">
            {notes.map((note) => (
              <li key={note.id} className="relative">
                <span
                  aria-hidden
                  className="absolute -left-[1.32rem] top-1.5 h-2 w-2 rounded-full bg-primary ring-4 ring-card"
                />
                <p className="text-sm">{note.body}</p>
                <p className="mt-1 text-xs text-muted-fg">
                  {note.author.display_name} · {formatTimeAgo(note.created_at)}
                </p>
              </li>
            ))}
          </ol>
        )}
        <AddNoteForm alertId={alertId} />
      </CardContent>
    </Card>
  )
})

function AddNoteForm({ alertId }: { alertId: string }) {
  const [body, setBody] = useState('')
  const addNote = useAddNote()

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (body.trim().length === 0) return
    addNote.mutate(
      { alert_id: alertId, body: body.trim() },
      { onSuccess: () => setBody('') },
    )
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-2">
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Add a note — recorded in the audit log…"
        className="min-h-[80px] rounded border border-input bg-bg p-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label="New note"
      />
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-fg">
          Notes are visible to your team and to compliance auditors.
        </p>
        <Button
          type="submit"
          size="sm"
          disabled={body.trim().length === 0 || addNote.isPending}
        >
          {addNote.isPending ? 'Adding…' : 'Add note'}
        </Button>
      </div>
    </form>
  )
}
